import type { ValuedInformation } from 'dashboard409a/services/shared-types';

export const DEFAULT_VISIBLE_FIELDS = [
  'valuationDate',
  'valuationFee',
  'valuationNetValue',
  'bookingDate',
  'renewalDate',
] as const;

export type FinancialFieldKey = 
  | 'valuationDate'
  | 'valuationFee'
  | 'valuationNetValue'
  | 'bookingDate'
  | 'renewalDate'
  | 'renewalRate'
  | 'split'
  | 'payment1'
  | 'payment1Date'
  | 'payment2'
  | 'payment2Date'
  | 'payment3'
  | 'payment3Date'
  | 'refund'
  | 'refundDate'
  | 'adjustment'
  | 'adjustmentDate'
  | 'badDebt'
  | 'badDebtDate';

export interface FinancialFieldConfig {
  key: FinancialFieldKey;
  label: string;
  isCurrency: boolean;
  getValue: (valuedInformation?: ValuedInformation) => string | number | undefined;
}

export const getFinancialFields = (
  valuedInformation?: ValuedInformation
): FinancialFieldConfig[] => [
  // Default visible fields (always shown when collapsed)
  {
    key: 'valuationDate',
    label: 'Valuation Date:',
    isCurrency: false,
    getValue: () => valuedInformation?.valuationDate,
  },
  {
    key: 'valuationFee',
    label: 'Valuation Fee:',
    isCurrency: true,
    getValue: () => valuedInformation?.valuationFee || 0,
  },
  {
    key: 'valuationNetValue',
    label: 'Net Value:',
    isCurrency: true,
    getValue: () => {
      if (valuedInformation?.valuationNetValue && valuedInformation.valuationNetValue !== '-') {
        return Number(valuedInformation.valuationNetValue) || 0;
      }
      return valuedInformation?.valuationNetValue || '-';
    },
  },
  {
    key: 'bookingDate',
    label: 'Booking Date:',
    isCurrency: false,
    getValue: () => valuedInformation?.bookingDate,
  },
  {
    key: 'renewalDate',
    label: 'Renewal Date:',
    isCurrency: false,
    getValue: () => valuedInformation?.renewalDate,
  },
  // Additional fields (shown when expanded)
  {
    key: 'renewalRate',
    label: 'Renewal Rate:',
    isCurrency: true,
    getValue: () => valuedInformation?.renewalRate,
  },
  {
    key: 'split',
    label: 'Split:',
    isCurrency: true,
    getValue: () => valuedInformation?.split,
  },
  {
    key: 'payment1',
    label: 'Payment 1:',
    isCurrency: true,
    getValue: () => valuedInformation?.payment1,
  },
  {
    key: 'payment1Date',
    label: 'Payment 1 Date:',
    isCurrency: false,
    getValue: () => valuedInformation?.payment1Date,
  },
  {
    key: 'payment2',
    label: 'Payment 2:',
    isCurrency: true,
    getValue: () => valuedInformation?.payment2,
  },
  {
    key: 'payment2Date',
    label: 'Payment 2 Date:',
    isCurrency: false,
    getValue: () => valuedInformation?.payment2Date,
  },
  {
    key: 'payment3',
    label: 'Payment 3:',
    isCurrency: true,
    getValue: () => valuedInformation?.payment3,
  },
  {
    key: 'payment3Date',
    label: 'Payment 3 Date:',
    isCurrency: false,
    getValue: () => valuedInformation?.payment3Date,
  },
  {
    key: 'refund',
    label: 'Refund:',
    isCurrency: true,
    getValue: () => valuedInformation?.refund,
  },
  {
    key: 'refundDate',
    label: 'Refund Date:',
    isCurrency: false,
    getValue: () => valuedInformation?.refundDate,
  },
  {
    key: 'adjustment',
    label: 'Adjustment:',
    isCurrency: true,
    getValue: () => valuedInformation?.adjustment,
  },
  {
    key: 'adjustmentDate',
    label: 'Adjustment Date:',
    isCurrency: false,
    getValue: () => valuedInformation?.adjustmentDate,
  },
  {
    key: 'badDebt',
    label: 'Bad Debt:',
    isCurrency: true,
    getValue: () => valuedInformation?.badDebt,
  },
  {
    key: 'badDebtDate',
    label: 'Bad Debt Date:',
    isCurrency: false,
    getValue: () => valuedInformation?.badDebtDate,
  },
];

